import { DOMParser } from '@xmldom/xmldom';
import * as toGeoJSON from '@mapbox/togeojson';
import { GPXData } from '../types';

export const parseGPX = (gpxString: string): GPXData => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(gpxString, 'text/xml');

  // Check for parsing errors
  const parseErrors = xmlDoc.getElementsByTagName('parsererror');
  if (parseErrors.length > 0) {
      console.error("Error parsing GPX XML:", parseErrors[0].textContent);
      // Return a default/empty structure or throw an error
      return { coordinates: [], metadata: {}, distances: [], totalDistance: 0 }; 
  }

  const geoJSON = toGeoJSON.gpx(xmlDoc);

  // Check if features exist and have geometry
  if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0 || !geoJSON.features[0].geometry) {
      console.error("Error converting GPX to GeoJSON or no features found.");
      return { coordinates: [], metadata: {}, distances: [], totalDistance: 0 };
  }

  // Extract coordinates including elevation from GeoJSON
  // The coordinates from togeojson are [lon, lat, ele]
  const coordinates: [number, number, number?][] = geoJSON.features[0].geometry.coordinates.map(
    (coord: number[]) => {
        // Ensure coord is an array and has at least 2 elements
        if (!Array.isArray(coord) || coord.length < 2) {
            console.warn('Skipping invalid coordinate:', coord);
            return null; // Skip invalid points
        }
        return [
            coord[0], // Longitude
            coord[1], // Latitude
            coord[2]  // Elevation (will be undefined if not present)
        ];
    }
  ).filter((c: [number, number, number?] | null): c is [number, number, number?] => c !== null); // Filter out skipped points and provide type guard

  // Calculate distances in METERS
  const distances: number[] = [0]; // Start with 0 distance for the first point
  let currentCumulativeDistance = 0;
  
  for (let i = 1; i < coordinates.length; i++) {
    const prev = coordinates[i - 1];
    const curr = coordinates[i];
    // Using the local calculateDistanceMeters (renamed and updated)
    const distanceIncrement = calculateDistanceMeters([prev[1], prev[0]], [curr[1], curr[0]]); 
    currentCumulativeDistance += distanceIncrement;
    distances.push(currentCumulativeDistance);
  }

  // Extract metadata
  const metadata = {
    name: geoJSON.features[0].properties?.name,
    desc: geoJSON.features[0].properties?.desc,
    time: geoJSON.features[0].properties?.time
  };

  return {
    coordinates, 
    metadata,
    distances,   // Distances are now in METERS
    totalDistance: currentCumulativeDistance // Total distance is in METERS
  };
};

// Renamed and updated to return meters
const calculateDistanceMeters = (point1: [number, number], point2: [number, number]): number => {
  const R = 6371e3; // Earth's radius in meters
  const dLat = toRad(point2[0] - point1[0]); // point[0] is Latitude
  const dLon = toRad(point2[1] - point1[1]); // point[1] is Longitude
  const lat1 = toRad(point1[0]);
  const lat2 = toRad(point2[0]);

  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Returns meters
};

const toRad = (value: number): number => {
  return value * Math.PI / 180;
}; 